import { useEffect, useRef, useState } from 'react';
import type { ObjectProposalGenerationType } from '@econtractor/types';
import * as apiDocument from 'api/apiDocument';
import { useObjectCard } from '../../context/ObjectCardProvider';
import useSettingsParams from 'hooks/useSettingsParams';
import { findAcceptedProposal } from './findAcceptedProposal';
import {
    getProposalProductShortages,
    getProposalServiceShortages,
} from './getProposalShortage';
import type { ProposalShortageItem, ProposalShortageKind } from './types';

interface UseProposalShortageResult {
    loading: boolean;
    proposalName: string;
    generation: ObjectProposalGenerationType | null;
    items: ProposalShortageItem[];
    hasShortage: boolean;
}

const useProposalShortage = (kind: ProposalShortageKind): UseProposalShortageResult => {
    const { objectId, vObject } = useObjectCard();
    const params = useSettingsParams();
    const [generation, setGeneration] = useState<ObjectProposalGenerationType | null>(null);
    const [items, setItems] = useState<ProposalShortageItem[]>([]);
    const [proposalName, setProposalName] = useState('');
    const [loadingGens, setLoadingGens] = useState(false);
    const [loadingCompare, setLoadingCompare] = useState(false);
    const loadedObjectIdRef = useRef(0);
    const compareSeqRef = useRef(0);

    useEffect(() => {
        const targetObjectId = Number(objectId || 0);
        if (!targetObjectId) {
            setGeneration(null);
            loadedObjectIdRef.current = 0;
            setLoadingGens(false);
            return;
        }

        let cancelled = false;
        const isInitial = loadedObjectIdRef.current !== targetObjectId;
        if (isInitial) setLoadingGens(true);

        void (async () => {
            const rows = await apiDocument.getObjectDocumentGenerations(targetObjectId);
            if (cancelled) return;
            setGeneration(findAcceptedProposal(rows || []));
            loadedObjectIdRef.current = targetObjectId;
            setLoadingGens(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [objectId]);

    useEffect(() => {
        if (!generation) {
            setItems([]);
            setProposalName('');
            setLoadingCompare(false);
            return;
        }

        const seq = ++compareSeqRef.current;
        const isInitialCompare = items.length === 0;
        if (isInitialCompare) setLoadingCompare(true);
        setProposalName(String(generation.proposalName || '').trim());

        void (async () => {
            try {
                const nextItems =
                    kind === 'products'
                        ? await getProposalProductShortages(generation, vObject?.stocks)
                        : getProposalServiceShortages(generation, vObject?.expendTeams, {
                              expends: vObject?.expends,
                              includeOtherInObjectCost: params.includeOtherInObjectCost,
                          });
                if (seq !== compareSeqRef.current) return;
                setItems(nextItems);
            } finally {
                if (seq === compareSeqRef.current) setLoadingCompare(false);
            }
        })();
        // items.length only gates initial spinner — avoid re-compare loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generation, vObject, kind, params.includeOtherInObjectCost]);

    return {
        // Don't hide an already-visible shortage while recalculating after object refresh
        loading: loadingGens || (loadingCompare && items.length === 0),
        proposalName,
        generation,
        items,
        hasShortage: items.length > 0,
    };
};

export default useProposalShortage;
